import React, { Suspense } from 'react';
import cx from 'classnames';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import { useLazyComponent } from '@trello/use-lazy-component';
import { usePopover } from '@trello/nachos/popover';
import { forNamespace } from '@trello/i18n';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { Controller } from 'app/scripts/controller';
import { toggleButler } from '../showButlerDirectory';
import styles from './ButlerBoardButtons.less';
import { useHasButlerAccess } from '../useHasButlerAccess';
import { ButlerAlert, showButlerAlert } from '../showButlerAlert';
import { Auth } from 'app/scripts/db/auth';
import { useButlerPluginDataQuery } from './ButlerPluginDataQuery.generated';

const format = forNamespace('butler');

export interface ButlerBoardButtonsProps {
  idBoard: string;
  idOrganization: string;
}

export const ButlerBoardButtons: React.FunctionComponent<ButlerBoardButtonsProps> = ({
  idBoard,
  idOrganization,
}) => {
  const { data } = useButlerPluginDataQuery({ variables: { idBoard } });
  const isLightBackground =
    data?.board?.prefs?.backgroundBrightness === 'light';

  const hasButlerAccess = useHasButlerAccess(idBoard);

  const getIconUrl = () => {
    return isLightBackground
      ? `${require('resources/images/butler/automation-dark.svg')}`
      : `${require('resources/images/butler/automation-light.svg')}`;
  };

  const ButlerBoardButtonPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "butler-board-button-popover" */ './ButlerBoardButtonPopover'
      ),
    { namedImport: 'ButlerBoardButtonPopover' },
  );

  const {
    popoverProps: butlerBoardBtnPopoverProps,
    triggerRef: butlerBoardBtnTriggerRef,
    toggle: toggleButlerBoardBtnPopover,
    push: pushButlerBoardBtnPopoverScreen,
    pop: popButlerBoardBtnPopoverScreen,
    hide: hideButlerBoardBtnPopover,
  } = usePopover<HTMLButtonElement>();

  const onClickButlerButton = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'automationBoardButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });

    const currentBoardView = Controller.getCurrentBoardView();
    if (Controller.showingAutomaticReports()) {
      currentBoardView?.closeAutomaticReports();
    } else if (!Auth.confirmed()) {
      showButlerAlert(ButlerAlert.Unauthorized);
    } else if (!currentBoardView?.isButlerViewActive()) {
      toggleButlerBoardBtnPopover();
      Analytics.sendScreenEvent({
        name: 'butlerReportInlineDialog',
      });
    } else {
      toggleButler();
    }
  };

  if (!hasButlerAccess) {
    return null;
  }

  return (
    <>
      <Button
        className={styles.butlerBoardButton}
        appearance={isLightBackground ? 'transparent-dark' : 'transparent'}
        // eslint-disable-next-line react/jsx-no-bind
        onClick={onClickButlerButton}
        ref={butlerBoardBtnTriggerRef}
        tabIndex={0}
      >
        <span
          className={cx('icon-sm', 'recolorable', styles.boardButtonIcon)}
          style={{
            backgroundImage: `url(${getIconUrl()})`,
          }}
        />
        <span>{format('automation')}</span>
      </Button>
      <ChunkLoadErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          {butlerBoardBtnPopoverProps.isVisible && (
            <ButlerBoardButtonPopover
              idBoard={idBoard}
              popoverProps={butlerBoardBtnPopoverProps}
              push={pushButlerBoardBtnPopoverScreen}
              pop={popButlerBoardBtnPopoverScreen}
              hide={hideButlerBoardBtnPopover}
            />
          )}
        </Suspense>
      </ChunkLoadErrorBoundary>
    </>
  );
};
